import { Epic, ofType } from "redux-observable";
import { catchError, map, mergeMap, of, takeUntil, merge, filter } from "rxjs";
import { IAction } from "../../interface/action.interface";
import { IErrorDto } from "../../interface/dto/error.dto";
import { IRootState } from "../../redux/root.reducer";
import { IIGSEReduxFactoryOptions } from "./igse-redux-factory-options";
import { IIGSEState } from "./igse-state";

export const createIGSERedux = <T>(options: IIGSEReduxFactoryOptions<T>) => {
    const { init, get, success, error } = options.actionNames;

    interface IInitAction extends IAction {
        type: typeof init;
    }

    interface IGetAction extends IAction {
        type: typeof get;
    }

    interface ISuccessAction extends IAction {
        type: typeof success;
        payload: T;
    }

    interface IErrorAction extends IAction {
        type: typeof error;
        payload: string;
    }

    type IIGSEAction = IInitAction | IGetAction | ISuccessAction | IErrorAction;

    const initialState: IIGSEState<T> = {
        data: undefined,
        error: undefined,
        isLoading: false,
    };

    const actions = {
        init: (): IInitAction => ({
            type: init,
            payload: {},
        }),
        get: (): IGetAction => ({
            type: get,
            payload: {},
        }),
        success: (result: T): ISuccessAction => ({
            type: success,
            payload: result,
        }),
        error: (err: string): IErrorAction => ({
            type: error,
            payload: err,
        }),
    };

    const reducer = (state = initialState, action: IIGSEAction) => {
        switch (action.type) {
            case get:
                return { isLoading: true, error: undefined };
            case error:
                return {
                    ...state,
                    error: action.payload as string,
                    isLoading: false,
                };
            case success:
                return {
                    ...state,
                    isLoading: false,
                    data: action.payload as T,
                };
            default:
                return state;
        }
    };

    const epic: Epic<IAction, IAction, IRootState> = (action$, state$) =>
        merge(
            action$.pipe(
                ofType<IInitAction, typeof init>(init),
                filter(
                    () =>
                        !options.selector ||
                        !options.selector(state$.value).data,
                ),
                mergeMap(() => of(actions.get())),
            ),
            action$.pipe(
                ofType<IGetAction, typeof get>(get),
                mergeMap(() =>
                    options.fetcher(state$.value).pipe(
                        takeUntil(
                            action$.pipe(ofType<IGetAction, typeof get>(get)),
                        ),
                        map((result) => actions.success(result)),
                        catchError((error: IErrorDto) =>
                            of(actions.error(error.message)),
                        ),
                    ),
                ),
            ),
        );

    return {
        actions,
        reducer,
        epic,
    };
};
